// MatchTile — Rippers green stacked match card for TV View Mode (shared by
// RR and Tournament schedules). Fixed internal regions so every card on a
// slate aligns: Court/Time · Team 1 · VS · Team 2 · SCORE · result.
// Doubles teams render as two player lines (no slash); custom team names
// are kept intact. Winning team gets a solid gold banner + check.
import React from 'react';
import { Check } from '@phosphor-icons/react';
import FitText from './FitText';
import { fmtEventTime } from '../../lib/scheduleTime';

const GREEN = '#0c4f38';
const GOLD = '#f2c94c';

function fmtTime(scheduledAt) {
  return fmtEventTime(scheduledAt);
}

// Lines to show for an entry: [team_name] when custom, else [p1, p2] / [p1].
export function entryLines(entry) {
  if (!entry) return null;
  if (entry.team_name) return [entry.team_name];
  const p1 = entry.player1_name; const p2 = entry.player2_name;
  if (p1 && p2) return [p1, p2];
  if (p1) return [p1];
  return entry.display_name ? [entry.display_name] : null;
}

function TeamRegion({ entry, isWinner, testid }) {
  const lines = entryLines(entry);
  const fg = isWinner ? GREEN : '#ffffff';
  return (
    <div className="flex-1 min-h-0 flex px-[4%]" data-testid={testid} data-winner={isWinner ? '1' : undefined}>
      <div
        className="flex-1 min-h-0 min-w-0 grid items-center rounded-xl"
        style={{
          gridTemplateColumns: 'clamp(16px, 1.5vw, 28px) 1fr clamp(16px, 1.5vw, 28px)',
          background: isWinner ? GOLD : 'transparent',
          padding: '0 clamp(4px, 0.5vw, 10px)',
          boxShadow: isWinner ? '0 2px 8px rgba(0,0,0,0.18)' : 'none',
        }}
      >
        <div />
        {lines ? (
          <FitText
            lines={lines.length > 1 ? lines : undefined}
            text={lines.length === 1 ? lines[0] : undefined}
            maxRatio={lines.length > 1 ? 0.42 : 0.46}
            maxWRatio={0.1}
            minPx={11}
            maxLines={lines.length > 1 ? 4 : 3}
            className="text-center uppercase tracking-[0.03em] font-bold"
            style={{ color: fg, lineHeight: 1.14 }}
            testid={`${testid}-name`}
          />
        ) : (
          <div className="text-center italic font-semibold text-white/60" style={{ fontSize: 'clamp(14px, 1.3vw, 24px)' }} data-testid={`${testid}-name`}>
            TBD
          </div>
        )}
        <div className="flex items-center justify-center">
          {isWinner && <Check weight="bold" style={{ color: GREEN, fontSize: 'clamp(18px, 1.5vw, 28px)' }} data-testid={`${testid}-winner`} />}
        </div>
      </div>
    </div>
  );
}

export default function MatchTile({ match, title }) {
  const time = fmtTime(match.scheduled_at);
  const court = match.court_label || null;
  const isCompleted = match.status === 'completed';
  const isBye = match.score_text === '(bye)';
  const w = match.winner_entry_id;
  const e1Wins = !!(w && match.entry1 && w === match.entry1.id);
  const e2Wins = !!(w && match.entry2 && w === match.entry2.id);
  const labelStyle = { color: GOLD, fontSize: 'clamp(11px, 1vw, 17px)' };

  return (
    <div
      data-testid={`view-match-tile-${match.id}`}
      className="h-full w-full min-h-0 rounded-2xl flex flex-col overflow-hidden"
      style={{
        background: GREEN,
        border: `2px solid ${GOLD}`,
        boxShadow: '0 8px 22px rgba(1, 53, 35, 0.22), 0 1px 3px rgba(1, 53, 35, 0.18)',
      }}
    >
      {title && (
        <div
          data-testid={`view-match-tile-${match.id}-title`}
          className="shrink-0 flex items-center justify-center px-[5%] text-center uppercase font-extrabold tracking-[0.12em] leading-tight"
          style={{
            height: 'clamp(34px, 5.2vh, 60px)', color: GOLD,
            background: 'rgba(0,0,0,0.20)', borderBottom: `1px solid ${GOLD}66`,
            fontSize: 'clamp(15px, 1.4vw, 26px)',
          }}
        >
          {title}
        </div>
      )}
      <div
        className="shrink-0 flex items-center justify-between px-[5%] uppercase tabular-nums font-bold tracking-[0.16em]"
        style={{ height: 'clamp(28px, 4.2vh, 42px)', borderBottom: `1px solid ${GOLD}66`, background: 'rgba(255,255,255,0.05)' }}
      >
        <div style={labelStyle} data-testid={`view-match-tile-${match.id}-court`}>
          {court ? `Court ${court}` : <span className="opacity-55">Court —</span>}
        </div>
        <div style={labelStyle} data-testid={`view-match-tile-${match.id}-time`}>
          {time || <span className="opacity-55">Time —</span>}
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col" style={{ padding: 'clamp(5px, 0.9vh, 10px) 0' }}>
        <TeamRegion entry={match.entry1} isWinner={e1Wins} testid={`view-match-tile-${match.id}-team1`} />
        <div
          className="shrink-0 flex items-center justify-center gap-3 uppercase tracking-[0.3em] font-bold"
          style={{ color: GOLD, height: 'clamp(22px, 3.6vh, 40px)', fontSize: 'clamp(14px, 1.25vw, 22px)' }}
        >
          <span className="block h-px w-[14%]" style={{ background: `${GOLD}66` }} />
          vs
          <span className="block h-px w-[14%]" style={{ background: `${GOLD}66` }} />
        </div>
        <TeamRegion entry={match.entry2} isWinner={e2Wins} testid={`view-match-tile-${match.id}-team2`} />
      </div>

      <div
        className="shrink-0 flex flex-col items-center justify-center px-[5%]"
        style={{ height: 'clamp(50px, 8.6vh, 84px)', borderTop: `1px solid ${GOLD}66`, background: 'rgba(0,0,0,0.10)' }}
        data-testid={`view-match-tile-${match.id}-score-block`}
      >
        <div className="uppercase tracking-[0.32em] font-bold leading-none" style={{ color: GOLD, fontSize: 'clamp(9px, 0.8vw, 13px)' }}>Score</div>
        <div
          className="font-bold tabular-nums leading-none text-center truncate max-w-full text-white"
          style={{ fontSize: 'clamp(18px, 1.7vw, 30px)', marginTop: 'clamp(4px, 0.7vh, 8px)' }}
          data-testid={`view-match-tile-${match.id}-score`}
        >
          {isBye
            ? <span className="italic text-white/70 font-semibold">Bye</span>
            : (isCompleted && match.score_text)
              ? match.score_text
              : <span className="text-white/90 tracking-[0.22em] uppercase font-semibold" style={{ fontSize: '0.72em' }}>Pending</span>}
        </div>
      </div>
    </div>
  );
}
